import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { MarketingSeo } from '@/pages/MarketingPages/MarketingSeo'
import {
  docDescription,
  docNav,
  docTitle,
  errors,
  examples,
  flowSteps,
  prizeHistorySample,
  scriptEmbedExample,
  scriptUrlExample,
  sessionPageExample,
  inputFields,
  operation,
  outputFields,
  profileFields,
  requestHeaders,
  requirementLabel,
  type DocField,
} from './content'
import styles from './IntegrationDocPage.module.css'

const sectionIds = docNav.map((item) => item.id)

function useActiveSection(ids: readonly string[]) {
  const [activeId, setActiveId] = useState(ids[0] ?? '')

  useEffect(() => {
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((section): section is HTMLElement => section !== null)

    if (sections.length === 0 || !('IntersectionObserver' in window)) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0]

        if (visible?.target.id) setActiveId(visible.target.id)
      },
      { rootMargin: '-20% 0px -55% 0px', threshold: [0.15, 0.4, 0.7] }
    )

    sections.forEach((section) => observer.observe(section))
    return () => observer.disconnect()
  }, [ids])

  return activeId
}

function CodeSample({ label, code }: { label: string; code: string }) {
  const [notice, setNotice] = useState<'idle' | 'copied' | 'error'>('idle')

  useEffect(() => {
    if (notice === 'idle') return
    const timeoutId = window.setTimeout(() => setNotice('idle'), 2000)
    return () => window.clearTimeout(timeoutId)
  }, [notice])

  async function copy() {
    try {
      await navigator.clipboard.writeText(code)
      setNotice('copied')
    } catch {
      setNotice('error')
    }
  }

  const buttonLabel =
    notice === 'copied' ? 'Copiado' : notice === 'error' ? 'Não foi possível copiar' : 'Copiar'

  return (
    <figure className={styles.sample}>
      <figcaption className={styles.sampleBar}>
        <span>{label}</span>
        <button type="button" onClick={() => void copy()} aria-live="polite">
          {buttonLabel} {label}
        </button>
      </figcaption>
      <pre>
        <code>{code}</code>
      </pre>
    </figure>
  )
}

function SchemaTable({
  caption,
  fields,
  showPlace,
}: {
  caption: string
  fields: DocField[]
  showPlace?: boolean
}) {
  return (
    <div className={styles.tableWrap}>
      <table>
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Nome</th>
            {showPlace ? <th scope="col">Local</th> : null}
            <th scope="col">Tipo</th>
            <th scope="col">Obrigatório</th>
            <th scope="col">Descrição</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => (
            <tr key={`${caption}-${field.name}`}>
              <th scope="row">
                <code>{field.name}</code>
              </th>
              {showPlace ? <td>{field.place}</td> : null}
              <td>
                <code>{field.type}</code>
              </td>
              <td data-requirement={field.requirement}>{requirementLabel[field.requirement]}</td>
              <td>{field.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function IntegrationDocPage() {
  const activeId = useActiveSection(sectionIds)

  return (
    <div className={styles.page}>
      <MarketingSeo title={docTitle} description={docDescription} path="/doc" />
      <a href="#conteudo" className={styles.skipLink}>
        Pular para o conteúdo
      </a>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.logo} aria-label="Sortee, página inicial">
            <img src="/logo/sortee.png" alt="Logo Sortee" width="2089" height="753" />
          </Link>
          <div className={styles.headerActions}>
            <p className={styles.headerLabel}>Referência da API</p>
            <a
              className={styles.exampleLink}
              href={sessionPageExample}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver exemplo da roleta
            </a>
          </div>
        </div>
      </header>
      <div className={styles.shell}>
        <nav className={styles.nav} aria-label="Nesta página">
          <p className={styles.navGroup}>Integrações</p>
          {docNav.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              aria-current={activeId === item.id ? 'true' : undefined}
            >
              {item.label}
            </a>
          ))}
        </nav>
        <main id="conteudo" tabIndex={-1}>
          <section id="visao-geral" className={styles.operation}>
            <div className={styles.opHead}>
              <span className={styles.method}>{operation.method}</span>
              <code className={styles.path}>{operation.path}</code>
            </div>
            <h1>{docTitle}</h1>
            <p>{operation.summary}</p>
            <p>
              A operação consulta o participante pelo <code>external_id</code>. Se o identificador
              existe, retorna o cadastro. Se não existe, cria o cadastro com o objeto{' '}
              <code>donor</code>. A resposta inclui o script da roleta ou da raspadinha e o perfil
              do participante na campanha.
            </p>
          </section>

          <section id="fluxo">
            <h2>Processamento</h2>
            <ol className={styles.steps}>
              {flowSteps.map((step, index) => (
                <li key={step}>
                  <span>{index + 1}</span>
                  <p>{step}</p>
                </li>
              ))}
            </ol>
          </section>

          <section id="endpoint">
            <h2>Requisição</h2>
            <p>
              Método <code>POST</code>. O token da campanha é enviado no header{' '}
              <code>X-Campaign-Token</code>. O corpo é JSON.
            </p>
            <CodeSample label="Chamada" code={requestHeaders} />
          </section>

          <section id="entrada">
            <h2>Parâmetros</h2>
            <SchemaTable caption="Parâmetros da requisição" fields={inputFields} showPlace />
          </section>

          <section id="saida">
            <h2>Resposta</h2>
            <p>
              Corpo JSON com <code>success</code> e <code>data</code>. <code>script_url</code> aponta
              para <code>roulette.js</code> ou <code>scratch.js</code> e já traz{' '}
              <code>external_id</code>, <code>campaign_id</code> e <code>cache</code>.
            </p>
            <CodeSample label="Script retornado" code={scriptUrlExample} />
            <CodeSample label="Inclusão no site" code={scriptEmbedExample} />
            <CodeSample label="Página de teste" code={sessionPageExample} />
            <a
              className={styles.exampleLink}
              href={sessionPageExample}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver exemplo da roleta
            </a>
            <SchemaTable caption="Campos de data" fields={outputFields} />
          </section>

          <section id="perfil">
            <h2>Objeto profile</h2>
            <p>
              <code>profile</code> descreve números, giros, prêmios e conquistas do participante na
              campanha. <code>game</code> assume <code>roulette</code> ou <code>scratch</code>.
            </p>
            <SchemaTable caption="Campos de profile" fields={profileFields} />
          </section>

          <section id="premio">
            <h2>Prêmio da Roleta API</h2>
            <p>
              O <code>external_id</code> do script é o id do cliente, sem prefixo. Quando a pessoa
              gira na página e vê o prêmio, o script confirma essa exibição. A partir daí o próximo
              giro é uma tentativa nova. Se o prêmio foi registrado pela API sem a pessoa estar na
              página, o primeiro giro dela mostra esse prêmio uma única vez.
            </p>
            <p>
              O sistema integrador consulta os prêmios com a chave da Roleta API. Em cada item,{' '}
              <code>status</code> igual a <code>won</code> indica vitória. <code>seen_at</code>{' '}
              vazio indica que a pessoa ainda não viu o prêmio na página.
            </p>
            <CodeSample label="Consulta de prêmio" code={prizeHistorySample} />
          </section>

          <section id="exemplos">
            <h2>Exemplos</h2>
            <p>
              Quando a campanha é de raspadinha, <code>game</code> retorna <code>scratch</code> e{' '}
              <code>script_url</code> aponta para o script correspondente. <code>profile.scratch</code>{' '}
              informa as tentativas do participante.
            </p>
            {examples.map((example) => (
              <article key={example.id} id={example.id} className={styles.example}>
                <h3>{example.title}</h3>
                <p>{example.summary}</p>
                <CodeSample label="Chamada" code={requestHeaders} />
                <CodeSample label="Corpo" code={example.body} />
                <CodeSample label="Resposta" code={example.response} />
              </article>
            ))}
          </section>

          <section id="erros">
            <h2>Erros</h2>
            <div className={styles.tableWrap}>
              <table>
                <caption>Condições de erro</caption>
                <thead>
                  <tr>
                    <th scope="col">Condição</th>
                    <th scope="col">Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((error) => (
                    <tr key={error.situation}>
                      <th scope="row">{error.situation}</th>
                      <td>{error.result}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}
